import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useChangeUserLevel, useUserLevels} from "@/hooks/useLevels";
import type {UserLevel} from "@/types/levels.types";
import {useState} from "react";
import {LEVEL_BADGE_VARIANT, LEVEL_NAMES, formatDate} from "../levels.utils";

export function UserLevelsTab() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [levelFilter, setLevelFilter] = useState<number | undefined>(undefined);
  const [changingUser, setChangingUser] = useState<UserLevel | null>(null);

  const {data, isLoading} = useUserLevels({page, limit, level: levelFilter});
  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<UserLevel>[] = [
    {
      key: "userId",
      header: "User ID",
      render: row => <span className="text-xs font-mono text-(--color-text-secondary)">{row.userId.slice(0, 8)}…</span>,
    },
    {
      key: "currentLevel",
      header: "Nivel",
      render: row => (
        <BaseBadge
          variant={LEVEL_BADGE_VARIANT[row.currentLevel] ?? "neutral"}
          label={LEVEL_NAMES[row.currentLevel] ?? `Level ${row.currentLevel}`}
        />
      ),
    },
    {
      key: "previousLevel",
      header: "Nivel Anterior",
      render: row =>
        row.previousLevel !== null ? (
          <BaseBadge
            variant={LEVEL_BADGE_VARIANT[row.previousLevel] ?? "neutral"}
            label={LEVEL_NAMES[row.previousLevel] ?? `Level ${row.previousLevel}`}
          />
        ) : (
          <span className="text-sm text-(--color-text-tertiary)">—</span>
        ),
    },
    {
      key: "promotedBy",
      header: "Tipo Promoción",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.promotedBy ?? "—"}</span>,
    },
    {
      key: "promotedAt",
      header: "Fecha Promoción",
      render: row => (
        <span className="text-xs text-(--color-text-tertiary)">
          {row.promotedAt ? formatDate(row.promotedAt) : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <BaseButton
          variant="secondary"
          size="sm"
          onClick={() => setChangingUser(row)}
        >
          Cambiar nivel
        </BaseButton>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <BaseCard padding="md">
        <div className="flex items-center gap-4">
          <select
            value={levelFilter ?? ""}
            onChange={e => {
              setLevelFilter(e.target.value === "" ? undefined : Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-2 rounded-md border border-(--color-border) bg-white text-sm text-(--color-text-primary) w-48"
          >
            <option value="">Todos los niveles</option>
            <option value="0">BASIC</option>
            <option value="1">REGULAR</option>
            <option value="2">VERIFIED</option>
          </select>
          <span className="text-sm text-(--color-text-secondary)">{pagination.total} usuarios</span>
        </div>
      </BaseCard>

      <BaseCard padding="none">
        <BaseTable<UserLevel>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron niveles de usuario."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {changingUser && (
        <ChangeLevelModal
          userLevel={changingUser}
          onClose={() => setChangingUser(null)}
        />
      )}
    </div>
  );
}

/* ── Change Level Modal ────────────────────────────────────── */

function ChangeLevelModal({userLevel, onClose}: {userLevel: UserLevel; onClose: () => void}) {
  const changeMutation = useChangeUserLevel();
  const [newLevel, setNewLevel] = useState(String(userLevel.currentLevel));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    const lvl = Number(newLevel);
    if (lvl === userLevel.currentLevel) {
      setError("El usuario ya se encuentra en ese nivel");
      return;
    }
    if (!reason.trim()) {
      setError("El motivo es requerido");
      return;
    }
    if (reason.length > 500) {
      setError("El motivo no puede exceder 500 caracteres");
      return;
    }

    try {
      await changeMutation.mutateAsync({userId: userLevel.userId, body: {newLevel: lvl, reason: reason.trim()}});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cambiar el nivel");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">
          Cambiar Nivel — {userLevel.userId.slice(0, 8)}…
        </h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center gap-3">
            <span className="text-sm text-(--color-text-secondary)">Nivel actual:</span>
            <BaseBadge
              variant={LEVEL_BADGE_VARIANT[userLevel.currentLevel] ?? "neutral"}
              label={LEVEL_NAMES[userLevel.currentLevel] ?? `Level ${userLevel.currentLevel}`}
            />
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-(--color-text-secondary)">Nuevo nivel</span>
            <select
              value={newLevel}
              onChange={e => setNewLevel(e.target.value)}
              className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
            >
              <option value="0">0 — BASIC</option>
              <option value="1">1 — REGULAR</option>
              <option value="2">2 — VERIFIED</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-(--color-text-secondary)">Motivo del cambio</span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Ej. Verificación completada vía ticket #12345"
              maxLength={500}
              rows={3}
              className="px-3 py-2 rounded-md border border-(--color-border) text-sm resize-none"
            />
            <span className="text-xs text-(--color-text-tertiary) text-right">{reason.length}/500</span>
          </label>

          {error && <p className="text-sm text-(--color-danger)">{error}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={changeMutation.isPending}
            >
              Cambiar nivel
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}
